import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { FastField, Field } from 'formik';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { InputLabel, Tabs, Tab } from '@material-ui/core';
import _e from 'common/localeUtil';
import TextInputField from './form/TextInputField';
import LabelValueComponent from './LabelValueComponent';
import FieldWrapper from './form/FieldWrapper';
import {
	getTemplateSectionConfig,
	setContactFormToTemplate,
	setContentGrapesJSComponent,
	setStyleGrapesJSComponent,
	setStyleGrapesJSComponents,
	setStyleGrapesJSComponentUsingAddStyle
} from '../utils';
import { a11yProps, TabPanel } from './tab/tab';

const useStyles = makeStyles(() => ({
	form: {
		'& .section-title': {
			marginBottom: '1rem'
		},
		'& .input': {
			height: '2.5rem'
		},
		'& .height2rem': {
			height: '2rem'
		}
	}
}));

const ContactSettings = ({ lightBackground = false, from }) => {
	const classes = useStyles();
	const [selectedTab, setSelectedTab] = useState(0);
	const contactForm = useStoreState(state => state.template.contactForm);
	const setContactForm = useStoreActions(actions => actions.template.setContactForm);
	const templateId = useStoreState(state => state.template.template);
	let templateConfig = getTemplateSectionConfig(templateId, 'contact');
	// should have some default values
	templateConfig = {
		receivingEmail: true,
		contactNumber: true,
		...templateConfig
	};

	const handleChange = (event, newValue) => {
		setSelectedTab(newValue);
	};

	useEffect(() => {
		// console.log('ContactSettings useEffect', contactForm);
		setContactFormToTemplate(contactForm);
	});

	const handleOnChange = social => value => {
		// console.log('ContactSettings handleOnChange', social, value);
		setContactForm({
			...contactForm,
			[social]: value
		});
		if (social === 'buttonText') {
			setContentGrapesJSComponent('#section-contact .button', value);
		} else if (social === 'receiving_email') {
			setContentGrapesJSComponent('.email-address', value);
		} else if (social === 'contact_number') {
			console.log('set contact', value);
			setContentGrapesJSComponent('.contact-number', value);
		}
	};

	const handleColorChanged = part => hex => {
		console.log('ContactSettings handleColorChanged', part, hex);
		if (part === 'header') {
			setStyleGrapesJSComponent('#section-contact .header', { color: hex });
		} else if (part === 'body') {
			setStyleGrapesJSComponent('#section-contact', { color: hex });
			setStyleGrapesJSComponents('#section-contact a', { color: hex });
			setStyleGrapesJSComponents('#section-contact .form-label', { color: hex });
		} else if (part === 'buttonTextColor') {
			setStyleGrapesJSComponent('#section-contact .button', { color: hex });
		} else if (part === 'buttonBackgroundColor') {
			setStyleGrapesJSComponent('#section-contact .button', {
				background: `${hex} !important;`
			});
		} else if (part === 'socialIconsColor') {
			setStyleGrapesJSComponentUsingAddStyle(`
				.social a i {
				  color: ${hex} !important;
				}
			`);
		}
	};

	const renderSocial = (faName, name, help) => {
		return (
			<div className="flex-center-space-between marginTopBottom16">
				<div className="marginRight10">
					<i className={`fa ${faName} socialIcon`} />
				</div>
				<div className="flex1">
					<TextInputField
						name={`contactForm.${name}`}
						placeholder=""
						onChangeCallBack={handleOnChange(name)}
						lightBackground={lightBackground}
						help={help}
					/>
				</div>
			</div>
		);
	};

	const {
		headerColor = '#000000',
		textColor = '#000000',
		buttonTextColor = '#000000',
		buttonBackgroundColor = '#000000',
		socialIconsColor = '#000000'
	} = contactForm || {};
	// console.log('ContactSettings rerender', from, headerColor, textColor);

	return (
		<Field name="contactForm" as={`div`}>
			<div className={classes.form}>
				<Tabs
					value={selectedTab}
					onChange={handleChange}
					indicatorColor="primary"
					textColor="primary"
					variant="scrollable"
					scrollButtons="auto"
					aria-label="scrollable auto tabs example"
				>
					<Tab label={_e('contact')} {...a11yProps(0)} />
					<Tab label={_e('appearance')} {...a11yProps(1)} />
				</Tabs>
				<TabPanel value={selectedTab} index={0}>
					{templateConfig && templateConfig?.title && (
						<LabelValueComponent
							label={<InputLabel>{_e('title')}</InputLabel>}
							value={<TextInputField name="title" placeholder="" />}
						/>
					)}
					{templateConfig && templateConfig?.receivingEmail && (
						<>
							<div className="section-title">{_e('receiving_email')}</div>
							<TextInputField
								name="contactForm.receiving_email"
								placeholder=""
								onChangeCallBack={handleOnChange('receiving_email')}
							/>
							<div className="height2rem" />
						</>
					)}
					{templateConfig && templateConfig?.contactNumber && (
						<>
							<div className="section-title">{_e('contact_number')}</div>
							<TextInputField
								name="contactForm.mobile_number"
								placeholder=""
								onChangeCallBack={handleOnChange('contact_number')}
							/>
							<div className="height2rem" />
						</>
					)}
					<div className="section-title">Social media links</div>
					{renderSocial(
						'fa-facebook',
						'facebook',
						'e.g. https://www.facebook.com/goalsmapper.sg'
					)}
					{renderSocial('fa-twitter', 'twitter', 'e.g. https://www.twitter.com')}
					{renderSocial(
						'fa-linkedin',
						'linkedin',
						'e.g. https://www.linkedin.com/company/goalsmapper/'
					)}
					{renderSocial(
						'fa-instagram',
						'instagram',
						'e.g. https://www.instagram.com/goalsmapper/'
					)}
					{renderSocial('fa-whatsapp', 'whatsapp', _e('whatsapp_desc'))}
					{renderSocial('fa-telegram', 'telegram', _e('telegram_desc'))}
				</TabPanel>
				<TabPanel value={selectedTab} index={1}>
					<LabelValueComponent
						label={<InputLabel>{_e('header_color')}</InputLabel>}
						value={
							<FastField
								defaultHex={headerColor}
								name="contactForm.headerColor"
								component={FieldWrapper}
								control="colorpicker"
								callback={handleColorChanged('header')}
								from={from}
							/>
						}
					/>
					<LabelValueComponent
						label={<InputLabel>{_e('text_color')}</InputLabel>}
						value={
							<FastField
								defaultHex={textColor}
								name="contactForm.textColor"
								component={FieldWrapper}
								control="colorpicker"
								callback={handleColorChanged('body')}
								from={from}
							/>
						}
					/>
					<LabelValueComponent
						label={<InputLabel>{_e('button_text_color')}</InputLabel>}
						value={
							<FastField
								defaultHex={buttonTextColor}
								name="contactForm.buttonTextColor"
								component={FieldWrapper}
								control="colorpicker"
								callback={handleColorChanged('buttonTextColor')}
								from={from}
							/>
						}
					/>
					<LabelValueComponent
						label={<InputLabel>{_e('button_background_color')}</InputLabel>}
						value={
							<FastField
								defaultHex={buttonBackgroundColor}
								name="contactForm.buttonBackgroundColor"
								component={FieldWrapper}
								control="colorpicker"
								callback={handleColorChanged('buttonBackgroundColor')}
								from={from}
							/>
						}
					/>
					<LabelValueComponent
						label={<InputLabel>{_e('social_media_icons_color')}</InputLabel>}
						value={
							<FastField
								defaultHex={socialIconsColor}
								name="contactForm.socialIconsColor"
								component={FieldWrapper}
								control="colorpicker"
								callback={handleColorChanged('socialIconsColor')}
								from={from}
							/>
						}
					/>
					<LabelValueComponent
						label={<InputLabel>{_e('button_label')}</InputLabel>}
						value={
							<TextInputField
								name="contactForm.buttonText"
								placeholder=""
								onChangeCallBack={handleOnChange('buttonText')}
							/>
						}
					/>
				</TabPanel>
			</div>
		</Field>
	);
};

ContactSettings.propTypes = {
	lightBackground: PropTypes.bool,
	from: PropTypes.string,
	sectionId: PropTypes.string
};

export default ContactSettings;
